// om-gaussian.ts — octahedral reduced-Gaussian grid support (ECMWF IFS 9km,
// "O1280"). The .om file is a flat 1-D point stream (dims [1, ~6.6M]); this
// module reconstructs the deterministic grid geometry (no metadata carries it)
// and resamples a window of points onto a regular lat/lon grid the existing GPU
// reprojection path can render.
//
// Octahedral ON: 2N latitude rows (N per hemisphere). Row nearest each pole has
// 20 longitude points, increasing by 4 per row toward the equator:
//   pl[i] = 16 + 4·i   for i = 1..N (pole→equator), mirrored across the equator.
// For N=1280 the total is 2·Σ₁..₁₂₈₀(16+4i) = 6,599,680 — matches the file.
// Latitudes are the 2N Gaussian (Legendre-root) latitudes, ordered north→south.
// Each row's points start at longitude 0° and step 360/pl[i] eastward.

export interface GaussianGrid {
  n: number // resolution N (1280 for O1280)
  nLat: number // 2N latitude rows
  lats: Float64Array // row latitudes in degrees, north→south (lats[0] ≈ +89.9)
  pl: Int32Array // points per row, length nLat
  offsets: Int32Array // cumulative point offset per row, length nLat+1 (offsets[nLat] = total)
}

const gridCache = new Map<number, GaussianGrid>()

// Gaussian latitudes (degrees, north→south): asin of the roots of the Legendre
// polynomial P_nLat, found by Newton iteration (gauleg). Symmetric about 0.
function gaussianLatitudesDeg(nLat: number): Float64Array {
  const lats = new Float64Array(nLat)
  const half = (nLat + 1) >> 1
  for (let i = 0; i < half; i++) {
    // initial guess for the (i+1)-th root, descending from near +1 (north)
    let x = Math.cos((Math.PI * (i + 0.75)) / (nLat + 0.5))
    for (let iter = 0; iter < 100; iter++) {
      let p0 = 1
      let p1 = x
      for (let k = 2; k <= nLat; k++) {
        const p2 = ((2 * k - 1) * x * p1 - (k - 1) * p0) / k
        p0 = p1
        p1 = p2
      }
      // P_nLat'(x) = nLat·(x·P_nLat − P_{nLat−1}) / (x²−1)
      const dp = (nLat * (x * p1 - p0)) / (x * x - 1)
      const dx = p1 / dp
      x -= dx
      if (Math.abs(dx) < 1e-15) break
    }
    const latDeg = (Math.asin(x) * 180) / Math.PI
    lats[i] = latDeg
    lats[nLat - 1 - i] = -latDeg
  }
  return lats
}

export function octahedralGrid(n: number): GaussianGrid {
  const cached = gridCache.get(n)
  if (cached) return cached
  const nLat = 2 * n
  const pl = new Int32Array(nLat)
  for (let i = 0; i < n; i++) {
    const count = 16 + 4 * (i + 1) // i is 0-based; row (i+1) from the pole
    pl[i] = count
    pl[nLat - 1 - i] = count
  }
  const offsets = new Int32Array(nLat + 1)
  for (let i = 0; i < nLat; i++) offsets[i + 1] = offsets[i] + pl[i]
  const grid: GaussianGrid = { n, nLat, lats: gaussianLatitudesDeg(nLat), pl, offsets }
  gridCache.set(n, grid)
  return grid
}

// Total points for an octahedral ON grid (used to detect O-resolution from a
// flat point count, e.g. 6,599,680 → O1280).
export function octahedralResolutionFromCount(count: number): number | null {
  // total = 2·Σ₁..N(16+4i) = 4N² + 36N ; solve 4N²+36N−count = 0
  const n = (-36 + Math.sqrt(36 * 36 + 16 * count)) / 8
  const ni = Math.round(n)
  return ni > 0 && 4 * ni * ni + 36 * ni === count ? ni : null
}

// Nearest-row index for a latitude (lats are descending north→south).
function rowForLat(lats: Float64Array, lat: number): number {
  let lo = 0
  let hi = lats.length - 1
  if (lat >= lats[0]) return 0
  if (lat <= lats[hi]) return hi
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (lats[mid] > lat) lo = mid
    else hi = mid
  }
  // lats descending: dist to lo = lats[lo]−lat, dist to hi = lat−lats[hi]
  return lats[lo] - lat <= lat - lats[hi] ? lo : hi
}

// Flat point index nearest to (lat, lon). lon in degrees (any range).
export function pointIndexAt(grid: GaussianGrid, lat: number, lon: number): number {
  const row = rowForLat(grid.lats, lat)
  const np = grid.pl[row]
  const lon360 = ((lon % 360) + 360) % 360
  let col = Math.round((lon360 / 360) * np) % np
  if (col < 0) col += np
  return grid.offsets[row] + col
}

// Inclusive Gaussian row range covering [latMin, latMax] (lats descend N→S, so
// the higher latitude is the lower row index).
export function latBandRows(grid: GaussianGrid, latMin: number, latMax: number): [number, number] {
  const r0 = rowForLat(grid.lats, latMax) // top (smaller index)
  const r1 = rowForLat(grid.lats, latMin) // bottom (larger index)
  return r0 <= r1 ? [r0, r1] : [r1, r0]
}

// Resample Gaussian point values onto a regular lat/lon grid (nearest neighbour).
// `values` may be a WINDOW of the full point stream; `pointOffset` is the global
// point index of values[0] (0 for a full field). Returns raw (pre-scale) ints,
// row-major, row0South (row 0 = latMin) — matching OmField conventions.
export function resampleGaussianToGrid(
  grid: GaussianGrid,
  values: ArrayLike<number>,
  bbox: [number, number, number, number],
  outNLon: number,
  outNLat: number,
  pointOffset = 0
): Int32Array {
  const [latMin, lonMin, latMax, lonMax] = bbox
  const out = new Int32Array(outNLat * outNLon)
  for (let r = 0; r < outNLat; r++) {
    const lat = latMin + (r / (outNLat - 1)) * (latMax - latMin)
    const row = rowForLat(grid.lats, lat)
    const np = grid.pl[row]
    const base = grid.offsets[row] - pointOffset
    for (let c = 0; c < outNLon; c++) {
      const lon = lonMin + (c / (outNLon - 1)) * (lonMax - lonMin)
      const lon360 = ((lon % 360) + 360) % 360
      let col = Math.round((lon360 / 360) * np) % np
      if (col < 0) col += np
      out[r * outNLon + c] = values[base + col]
    }
  }
  return out
}
